import React, { useMemo, useState } from 'react';
import { SatelliteImageTask } from '../../models/satellite';
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';
import { Dialog, Transition } from '@headlessui/react';
import { handleApiError } from '../../api-client/error_handler';
import axios from 'axios';

interface Props {
  tasks: SatelliteImageTask[];
  setTasks: React.Dispatch<React.SetStateAction<SatelliteImageTask[]>>;
  loading: boolean;
  forAdminView?: boolean;
}

type SortKey = 'name' | 'datetime' | 'status' | 'cost';
type SortDir = 'asc' | 'desc';

export const SatelliteImageRequestTable: React.FC<Props> = ({ tasks, setTasks, loading, forAdminView = false }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<SatelliteImageTask | null>(null);
  const [dialogType, setDialogType] = useState<'payment' | 'costChange' | null>(null);
  const [newCost, setNewCost] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadTask, setUploadTask] = useState<SatelliteImageTask | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [isDownloadingId, setIsDownloadingId] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<'ALL'|'PENDING'|'COMPLETED'|'FAILED'>('ALL');
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('datetime');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const fmtEUR = useMemo(() => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }), []);
  const visible = useMemo(() => {
    const filtered = tasks.filter(t => {
      const statusOk = statusFilter === 'ALL' ? true : t.status === statusFilter;
      const queryOk = q
        ? (t.name?.toLowerCase().includes(q.toLowerCase()) ||
           t.user_email?.toLowerCase().includes(q.toLowerCase()) ||
           t.org_name?.toLowerCase().includes(q.toLowerCase()))
        : true;
      return statusOk && queryOk;
    });

    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') cmp = (a.name || '').localeCompare(b.name || '');
      if (sortKey === 'status') cmp = (a.status || '').localeCompare(b.status || '');
      if (sortKey === 'datetime') cmp = new Date(a.datetime).getTime() - new Date(b.datetime).getTime();
      if (sortKey === 'cost') cmp = (a.task_cost ?? 0) - (b.task_cost ?? 0);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [tasks, q, statusFilter, sortKey, sortDir]);

  const askToPay = (task: SatelliteImageTask) => {
    setSelectedTask(task);
    setDialogType('payment');
    setIsDialogOpen(true);
  };

  const confirmPayment = async () => {
    if (!selectedTask) return;
    setIsProcessing(true);
    try {
      const token = await getAccessTokenSilently();
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/satellite/pay-task`,
        { task_id: selectedTask.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTasks(prev => prev.map(t => t.id === selectedTask.id ? { ...t, is_paid: true } : t));
      toast.success('Payment successful.');
    } catch (e) {
      toast.error(handleApiError(e));
    } finally {
      setIsProcessing(false);
      setIsDialogOpen(false);
      setSelectedTask(null);
      setDialogType(null);
    }
  };

  const openUploadDialog = (task: SatelliteImageTask) => {
    setUploadTask(task);
    setPreviewFile(null);
    setOriginalFile(null);
    setIsUploadDialogOpen(true);
  };

  const uploadAsset = async (file: File, variant: 'preview' | 'original', token: string) => {
    const fileKey = `satellite_images/${variant}/${new Date().toISOString()}_${file.name}`;
    const { data: presignData } = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/generate-presigned-url`,
      { file_key: fileKey, content_type: file.type },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!presignData.url) throw new Error(presignData.message || 'Failed to get upload URL');
    await axios.put(presignData.url, file, { headers: { 'Content-Type': file.type } });
    return fileKey;
  };

  const handleFulfillWithPreview = async () => {
    if (!uploadTask || !previewFile) {
      toast.error('Preview image is required.');
      return;
    }
    setIsFileUploading(true);
    try {
      const token = await getAccessTokenSilently();
      const previewKey = await uploadAsset(previewFile, 'preview', token);
      let originalKey: string | undefined;
      if (originalFile) {
        originalKey = await uploadAsset(originalFile, 'original', token);
      }
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/satellite/fulfill-task`,
        {
          task_id: uploadTask.id,
          preview_file_key: previewKey,
          file_key: originalKey ?? previewKey
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTasks(prev => prev.map(t => t.id === uploadTask.id ? { ...t, status: 'COMPLETED' } : t));
      toast.success('Preview uploaded and task fulfilled.');
      setIsUploadDialogOpen(false);
      setUploadTask(null);
    } catch (e) {
      toast.error(handleApiError(e));
    } finally {
      setIsFileUploading(false);
    }
  };

  const handleDownload = async (task: SatelliteImageTask) => {
    setIsDownloadingId(task.id);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/satellite/download-image`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { task_id: task.id }
        }
      );
      if (!data.url) throw new Error(data.message || 'Download failed');
      window.open(data.url, '_blank');
    } catch (e) {
      toast.error(handleApiError(e));
    } finally {
      setIsDownloadingId(null);
    }
  };

  const askToChangeCost = (task: SatelliteImageTask, cost: number) => {
    if (!Number.isFinite(cost) || cost <= 0) return;
    if (task.task_cost === cost) return;
    setSelectedTask(task);
    setNewCost(cost);
    setDialogType('costChange');
    setIsDialogOpen(true);
  };

  const confirmTaskCostChange = async () => {
    if (!selectedTask || !newCost) return;
    setIsProcessing(true);
    try {
      const token = await getAccessTokenSilently();
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/satellite/change-task-price`,
        { task_id: selectedTask.id, price_amount: newCost },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTasks(prev => prev.map(t => t.id === selectedTask.id ? { ...t, task_cost: newCost } : t));
      toast.success('Task cost updated.');
    } catch (e) {
      toast.error(handleApiError(e));
    } finally {
      setIsProcessing(false);
      setIsDialogOpen(false);
      setSelectedTask(null);
      setDialogType(null);
      setNewCost(null);
    }
  };

  const headerCell = (label: string, key?: SortKey) => {
    const isActive = key && sortKey === key;
    const nextDir: SortDir = isActive ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
    return (
      <th
        className="px-4 py-2 border bg-slate-50 text-left text-xs font-semibold uppercase text-slate-600"
        onClick={key ? () => { setSortKey(key); setSortDir(nextDir); } : undefined}
        style={{ cursor: key ? 'pointer' : 'default' }}
        title={key ? `Sort by ${label}` : undefined}
      >
        <div className="flex items-center gap-1">
          <span>{label}</span>
          {key && (
            <svg className={`h-3 w-3 ${isActive ? 'opacity-100' : 'opacity-30'}`} viewBox="0 0 24 24" fill="currentColor">
              {isActive && sortDir === 'asc'
                ? <path d="M7 14l5-5 5 5z" />
                : <path d="M7 10l5 5 5-5z" />}
            </svg>
          )}
        </div>
      </th>
    );
  };

  const copyCoords = (lat: number, lon: number) => {
    navigator.clipboard.writeText(`${lat}, ${lon}`);
    toast.success('Coordinates copied');
  };

  const openMap = (lat: number, lon: number) => {
    window.open(`https://www.google.com/maps?q=${lat},${lon}`, '_blank');
  };

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      {/* toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
          >
            {['ALL','PENDING','COMPLETED','FAILED'].map(s => <option key={s}>{s}</option>)}
          </select>
          <input
            type="search"
            value={q}
            placeholder="Search name, org, user…"
            onChange={(e) => setQ(e.target.value)}
            className="w-64 rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
          />
        </div>
        <div className="text-xs text-slate-500">
          Showing <span className="font-semibold text-slate-700">{visible.length}</span> of {tasks.length}
        </div>
      </div>

      <table className={`w-full ${forAdminView ? 'table-auto' : 'min-w-[1200px]'}`}>
        <thead>
          <tr>
            {headerCell('Name', 'name')}
            {headerCell('Date', 'datetime')}
            {forAdminView && headerCell('Org Name')}
            {forAdminView && headerCell('Created By')}
            {headerCell('Longitude, Latitude')}
            {headerCell('Status', 'status')}
            {headerCell('Image')}
            {forAdminView && headerCell('Paid')}
            {forAdminView ? headerCell('Task Cost', 'cost') : headerCell('Action')}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <tr key={i} className="border-t animate-pulse">
                {Array.from({ length: forAdminView ? 9 : 7 }).map((__, j) => (
                  <td key={j} className="px-4 py-3">
                    <div className="h-4 rounded bg-slate-200" />
                  </td>
                ))}
              </tr>
            ))
          ) : visible.length === 0 ? (
            <tr>
              <td colSpan={forAdminView ? 9 : 7} className="px-4 py-6 text-center text-slate-500">
                No tasks match your filters
              </td>
            </tr>
          ) : (
            visible.map(task => {
              const downloading = isDownloadingId === task.id;
              return (
                <tr key={task.id} className="border-t">
                  <td className="px-4 py-2">{task.name}</td>
                  <td className="px-4 py-2">
                    {new Date(task.datetime).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}{' '}
                    {new Date(task.datetime).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  {forAdminView && <td className="px-4 py-2">{task.org_name}</td>}
                  {forAdminView && <td className="px-4 py-2">{task.user_email}</td>}
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{`${task.longitude}, ${task.latitude}`}</span>
                      <button className="rounded bg-slate-100 px-2 py-0.5 text-xs hover:bg-slate-200" onClick={() => copyCoords(parseFloat(task.latitude), parseFloat(task.longitude))}>Copy</button>
                      <button className="rounded bg-slate-100 px-2 py-0.5 text-xs hover:bg-slate-200" onClick={() => openMap(parseFloat(task.latitude), parseFloat(task.longitude))}>Map</button>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded ${
                        task.status === 'COMPLETED'
                          ? 'bg-emerald-100 text-emerald-700'
                          : task.status === 'FAILED'
                          ? 'bg-rose-100 text-rose-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {task.status}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {task.status === 'COMPLETED' ? (
                      <button
                        onClick={() => handleDownload(task)}
                        className="text-blue-600 hover:underline disabled:opacity-50"
                        disabled={downloading}
                      >
                        {downloading ? 'Downloading…' : 'Download'}
                      </button>
                    ) : forAdminView ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openUploadDialog(task)}
                          className="rounded-full bg-slate-800 px-3 py-1 text-sm text-white shadow hover:bg-black disabled:opacity-50"
                          disabled={isFileUploading}
                        >
                          {isFileUploading ? 'Uploading…' : 'Upload'}
                        </button>
                      </div>
                    ) : (
                      <span className="text-slate-500">Not ready</span>
                    )}
                  </td>
                  {forAdminView && <td className="px-4 py-2">{task.is_paid ? 'Yes' : 'No'}</td>}
                  {forAdminView ? (
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        defaultValue={task.task_cost || 0}
                        onBlur={(e) => askToChangeCost(task, parseFloat(e.target.value))}
                        className="w-28 rounded border px-2 py-1"
                      />
                    </td>
                  ) : (
                    <td className="px-4 py-2">
                      {task.task_cost ? (
                        task.is_paid ? (
                          <span className="text-emerald-600">Paid</span>
                        ) : (
                          <button
                            onClick={() => askToPay(task)}
                            className="rounded-full bg-blue-600 px-3 py-1 text-sm text-white shadow hover:bg-blue-700"
                          >
                            Pay {fmtEUR.format(task.task_cost)}
                          </button>
                        )
                      ) : (
                        <span className="text-slate-500">N/A</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* confirm modal */}
      <Transition show={isDialogOpen} as={React.Fragment}>
        <Dialog onClose={() => !isProcessing && setIsDialogOpen(false)} className="relative z-[9999]">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-150"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-2xl ring-1 ring-slate-100">
                  <Dialog.Title className="text-lg font-semibold text-slate-900">
                    {dialogType === 'payment' ? 'Confirm Payment' : 'Confirm Task Cost Change'}
                  </Dialog.Title>
                  <div className="mt-2 text-sm text-slate-600">
                    {dialogType === 'payment' ? (
                      <>Are you sure you want to pay <b>{fmtEUR.format(selectedTask?.task_cost || 0)}</b>? This will deduct from your credits.</>
                    ) : (
                      <>Change cost to <b>{fmtEUR.format(newCost || 0)}</b>?</>
                    )}
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      onClick={() => setIsDialogOpen(false)}
                      disabled={isProcessing}
                      className="rounded-full bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-300 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={dialogType === 'payment' ? confirmPayment : confirmTaskCostChange}
                      disabled={isProcessing}
                      className="rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {isProcessing ? 'Processing…' : 'Confirm'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* upload modal */}
      <Transition show={isUploadDialogOpen} as={React.Fragment}>
        <Dialog onClose={() => !isFileUploading && setIsUploadDialogOpen(false)} className="relative z-[9999]">
          <Transition.Child as={React.Fragment} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-150"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-2xl ring-1 ring-slate-100">
                  <Dialog.Title className="text-lg font-semibold text-slate-900">
                    Upload satellite imagery preview
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-slate-600">
                    Provide a lightweight preview (required) and optionally attach the original high-resolution file for internal records.
                  </p>
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 uppercase">Preview image (required)</label>
                      <input
                        type="file"
                        accept="image/*,.jpg,.jpeg,.png,.webp"
                        className="mt-1 block w-full rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm"
                        onChange={(e) => setPreviewFile(e.target.files?.[0] || null)}
                        disabled={isFileUploading}
                      />
                      <p className="mt-1 text-xs text-slate-500">Share a compressed JPG/PNG that end users can download.</p>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 uppercase">Original image (optional)</label>
                      <input
                        type="file"
                        accept=".tif,.tiff,.zip,image/*"
                        className="mt-1 block w-full rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm"
                        onChange={(e) => setOriginalFile(e.target.files?.[0] || null)}
                        disabled={isFileUploading}
                      />
                      <p className="mt-1 text-xs text-slate-500">Stored for CivAPI reference only. Not shared with SDK users.</p>
                    </div>
                  </div>
                  <div className="mt-5 flex justify-end gap-3">
                    <button
                      onClick={() => setIsUploadDialogOpen(false)}
                      className="rounded-full bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-300 disabled:opacity-50"
                      disabled={isFileUploading}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleFulfillWithPreview}
                      className="rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                      disabled={isFileUploading || !previewFile}
                    >
                      {isFileUploading ? 'Uploading…' : 'Save'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};
